import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken, Config } from '../../services/config';
import profileService, { UserProfile as IUserProfile, UpdateProfileData, ChangePasswordData } from '../../services/profile';
import ImageCropModal from '../../components/ImageCropModal';
import { useAuth } from '../../contexts/AuthContext';
import './UserProfile.css';

/**
 * Constantes de validação
 */
const PASSWORD_MIN_LENGTH = 8;
const PHONE_MIN_DIGITS = 10;
const PHONE_MAX_DIGITS = 11;

/**
 * Componente de página para edição do perfil do usuário
 * Permite editar dados pessoais e alterar a senha
 */
const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'dados' | 'senha'>('dados');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<IUserProfile | null>(null);

  // Dados do formulário de perfil
  const [formData, setFormData] = useState<UpdateProfileData>({
    nome: '',
    telefone: '',
    cargo: ''
  });

  // Dados do formulário de senha
  const [passwordData, setPasswordData] = useState<ChangePasswordData>({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: ''
  });

  const [passwordStrength, setPasswordStrength] = useState<'fraca' | 'media' | 'forte' | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados para foto de perfil
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);

  /**
   * Carrega os dados do perfil ao montar o componente
   */
  useEffect(() => {
    loadProfile();
  }, []);

  /**
   * Carrega os dados do perfil do usuário
   */
  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await profileService.getProfile();
      setProfile(data);
      setFormData({
        nome: data.nome || '',
        telefone: data.telefone || '',
        cargo: data.cargo || ''
      });
      // Carregar foto de perfil se existir
      if (data.foto_perfil_url) {
        setFotoPreview(data.foto_perfil_url);
      }
    } catch (error: any) {
      console.error('❌ UserProfile.loadProfile - Erro ao carregar perfil:', error);

      let errorMessage = 'Erro ao carregar perfil';
      if (error.response) {
        const responseData = error.response.data;
        errorMessage = responseData?.message || responseData?.error || `Erro ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'Erro de conexão. Verifique sua internet.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Tempo de requisição esgotado.';
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Atualiza os valores do formulário de perfil
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Atualiza os valores do formulário de senha
   */
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calcula força da senha apenas para nova senha
    if (name === 'novaSenha') {
      calculatePasswordStrength(value);
    }
  };

  /**
   * Calcula a força da senha
   */
  const calculatePasswordStrength = (password: string) => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }

    let strength = 0;
    if (password.length >= PASSWORD_MIN_LENGTH) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 1) setPasswordStrength('fraca');
    else if (strength <= 3) setPasswordStrength('media');
    else setPasswordStrength('forte');
  };

  /**
   * Valida os dados do formulário de perfil
   */
  const validateProfileForm = (): boolean => {
    if (!formData.nome || formData.nome.trim() === '') {
      alert('O nome é obrigatório');
      return false;
    }

    if (formData.telefone && !/^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(formData.telefone)) {
      const phoneNumbers = formData.telefone.replace(/\D/g, '');
      if (phoneNumbers.length < PHONE_MIN_DIGITS || phoneNumbers.length > PHONE_MAX_DIGITS) {
        alert('Telefone inválido. Use o formato válido com DDD');
        return false;
      }
    }

    return true;
  };

  /**
   * Valida os dados do formulário de senha
   */
  const validatePasswordForm = (): boolean => {
    if (!passwordData.senhaAtual) {
      alert('Digite sua senha atual');
      return false;
    }

    if (!passwordData.novaSenha) {
      alert('Digite a nova senha');
      return false;
    }

    if (passwordData.novaSenha.length < PASSWORD_MIN_LENGTH) {
      alert(`A nova senha deve ter no mínimo ${PASSWORD_MIN_LENGTH} caracteres`);
      return false;
    }

    if (passwordData.novaSenha !== passwordData.confirmarSenha) {
      alert('A nova senha e a confirmação não coincidem');
      return false;
    }

    if (passwordData.senhaAtual === passwordData.novaSenha) {
      alert('A nova senha deve ser diferente da senha atual');
      return false;
    }

    return true;
  };

  /**
   * Salva as alterações do perfil
   */
  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateProfileForm()) {
      return;
    }

    try {
      setSaving(true);
      const updated = await profileService.updateProfile(formData);
      setProfile(updated);
      alert('Perfil atualizado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Altera a senha do usuário
   */
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      setSaving(true);
      await profileService.changePassword(passwordData);
      alert('Senha alterada com sucesso!');

      // Limpa o formulário de senha
      setPasswordData({
        senhaAtual: '',
        novaSenha: '',
        confirmarSenha: ''
      });
      setPasswordStrength(null);
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      alert(error.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Formata o telefone conforme o usuário digita
   */
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  /**
   * Manipula a seleção de arquivo de foto
   */
  const handleFotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione um arquivo de imagem válido');
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5MB');
      return;
    }

    setSelectedImageFile(file);
    setShowCropModal(true);
  };

  /**
   * Manipula o upload da imagem cortada
   */
  const handleCropComplete = async (croppedBlob: Blob, fileName: string) => {
    try {
      setUploadingFoto(true);
      setShowCropModal(false);

      const token = getToken();
      const formData = new FormData();
      formData.append('file', croppedBlob, fileName);

      const response = await axios.post(
        `${Config.API_BASE_URL}/usuarios/foto-perfil`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setFotoPreview(response.data.data.foto_perfil_url);
        alert('Foto de perfil atualizada com sucesso!');

        // Atualizar o contexto de autenticação com a nova foto
        updateUser({ foto_perfil_url: response.data.data.foto_perfil_url });

        // Recarregar perfil para atualizar os dados
        await loadProfile();
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload da foto:', error);
      alert(error.response?.data?.message || 'Erro ao enviar foto de perfil');
    } finally {
      setUploadingFoto(false);
      setSelectedImageFile(null);
    }
  };

  /**
   * Usa a imagem original sem cortar
   */
  const handleUseOriginal = async (file: File) => {
    try {
      setUploadingFoto(true);
      setShowCropModal(false);

      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${Config.API_BASE_URL}/usuarios/foto-perfil`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setFotoPreview(response.data.data.foto_perfil_url);
        alert('Foto de perfil atualizada com sucesso!');

        // Atualizar o contexto de autenticação com a nova foto
        updateUser({ foto_perfil_url: response.data.data.foto_perfil_url });

        // Recarregar perfil para atualizar os dados
        await loadProfile();
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload da foto:', error);
      alert(error.response?.data?.message || 'Erro ao enviar foto de perfil');
    } finally {
      setUploadingFoto(false);
      setSelectedImageFile(null);
    }
  };

  /**
   * Deleta a foto de perfil
   */
  const handleDeleteFoto = async () => {
    if (!window.confirm('Tem certeza que deseja remover sua foto de perfil?')) {
      return;
    }

    try {
      setUploadingFoto(true);
      const token = getToken();

      await axios.delete(
        `${Config.API_BASE_URL}/usuarios/foto-perfil`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setFotoPreview(null);
      alert('Foto de perfil removida com sucesso!');

      // Atualizar o contexto de autenticação removendo a foto
      updateUser({ foto_perfil_url: undefined });

      // Recarregar perfil para atualizar os dados
      await loadProfile();
    } catch (error: any) {
      console.error('Erro ao deletar foto:', error);
      alert(error.response?.data?.message || 'Erro ao remover foto de perfil');
    } finally {
      setUploadingFoto(false);
    }
  };

  /**
   * Obtém as iniciais do usuário para exibir no avatar
   */
  const getUserInitials = (nome: string): string => {
    return nome
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="user-profile-container">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Carregando perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      <div className="user-profile-header">
        <div className="header-content">
          <h1>
            <i className="fas fa-user-circle"></i>
            Meu Perfil
          </h1>
          <p>Gerencie suas informações pessoais e segurança</p>
        </div>
      </div>

      <div className="user-profile-tabs">
        <button
          className={`tab-button ${activeTab === 'dados' ? 'active' : ''}`}
          onClick={() => setActiveTab('dados')}
        >
          <i className="fas fa-user"></i>
          Dados Pessoais
        </button>
        <button
          className={`tab-button ${activeTab === 'senha' ? 'active' : ''}`}
          onClick={() => setActiveTab('senha')}
        >
          <i className="fas fa-key"></i>
          Alterar Senha
        </button>
      </div>

      <div className="user-profile-content">
        {activeTab === 'dados' && (
          <form onSubmit={handleSaveProfile} className="profile-form">
            {/* Seção de Foto de Perfil */}
            <div className="form-section">
              <h2>Foto de Perfil</h2>
              <div className="profile-photo-section">
                <div className="profile-photo-preview">
                  {fotoPreview ? (
                    <img src={fotoPreview} alt="Foto de perfil" className="profile-photo" />
                  ) : (
                    <div className="profile-photo-placeholder">
                      {profile?.nome ? getUserInitials(profile.nome) : 'U'}
                    </div>
                  )}
                </div>
                <div className="profile-photo-actions">
                  <label className="btn-upload">
                    <i className="fas fa-upload"></i>
                    {fotoPreview ? 'Alterar Foto' : 'Adicionar Foto'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFotoSelect}
                      style={{ display: 'none' }}
                      disabled={uploadingFoto}
                    />
                  </label>
                  {fotoPreview && (
                    <button
                      type="button"
                      className="btn-delete"
                      onClick={handleDeleteFoto}
                      disabled={uploadingFoto}
                    >
                      <i className="fas fa-trash"></i>
                      Remover Foto
                    </button>
                  )}
                  {uploadingFoto && (
                    <span className="upload-status">
                      <i className="fas fa-spinner fa-spin"></i>
                      Enviando...
                    </span>
                  )}
                </div>
                <p className="profile-photo-hint">
                  Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
                </p>
              </div>
            </div>

            <div className="form-section">
              <h2>Informações Básicas</h2>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="nome">
                    Nome Completo <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    placeholder="Digite seu nome completo"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">E-mail</label>
                  <input
                    type="email"
                    id="email"
                    value={profile?.email || ''}
                    disabled
                    className="input-disabled"
                    title="O e-mail não pode ser alterado"
                  />
                  <small className="form-hint">O e-mail não pode ser alterado</small>
                </div>

                <div className="form-group">
                  <label htmlFor="telefone">Telefone</label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={(e) => {
                      const formatted = formatPhone(e.target.value);
                      setFormData(prev => ({ ...prev, telefone: formatted }));
                    }}
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cargo">Cargo</label>
                  <input
                    type="text"
                    id="cargo"
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleInputChange}
                    placeholder="Digite seu cargo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tipo">Tipo de Usuário</label>
                  <input
                    type="text"
                    id="tipo"
                    value={profile?.tipo === 'admin' ? 'Administrador' : profile?.tipo === 'supervisor' ? 'Supervisor' : 'Operador'}
                    disabled
                    className="input-disabled"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                <i className="fas fa-times"></i>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'senha' && (
          <form onSubmit={handleChangePassword} className="password-form">
            <div className="form-section">
              <h2>Alterar Senha</h2>
              <p className="section-description">
                Por segurança, digite sua senha atual e escolha uma nova senha forte.
              </p>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="senhaAtual">
                    Senha Atual <span className="required">*</span>
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      id="senhaAtual"
                      name="senhaAtual"
                      value={passwordData.senhaAtual}
                      onChange={handlePasswordChange}
                      placeholder="Digite sua senha atual"
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      <i className={showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="novaSenha">
                    Nova Senha <span className="required">*</span>
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="novaSenha"
                      name="novaSenha"
                      value={passwordData.novaSenha}
                      onChange={handlePasswordChange}
                      placeholder="Digite a nova senha"
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      <i className={showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                    </button>
                  </div>
                  {passwordStrength && (
                    <div className={`password-strength ${passwordStrength}`}>
                      <div className="strength-bar">
                        <div className="strength-fill"></div>
                      </div>
                      <span className="strength-text">
                        Senha {passwordStrength === 'fraca' ? 'Fraca' : passwordStrength === 'media' ? 'Média' : 'Forte'}
                      </span>
                    </div>
                  )}
                  <small className="form-hint">
                    A senha deve ter no mínimo {PASSWORD_MIN_LENGTH} caracteres, incluindo letras, números e caracteres especiais
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="confirmarSenha">
                    Confirmar Nova Senha <span className="required">*</span>
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmarSenha"
                      name="confirmarSenha"
                      value={passwordData.confirmarSenha}
                      onChange={handlePasswordChange}
                      placeholder="Confirme a nova senha"
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setPasswordData({
                    senhaAtual: '',
                    novaSenha: '',
                    confirmarSenha: ''
                  });
                  setPasswordStrength(null);
                }}
                disabled={saving}
              >
                <i className="fas fa-times"></i>
                Limpar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Alterando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-key"></i>
                    Alterar Senha
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Modal de Crop de Imagem */}
      {showCropModal && selectedImageFile && (
        <ImageCropModal
          imageFile={selectedImageFile}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
          onUseOriginal={handleUseOriginal}
        />
      )}
    </div>
  );
};

export default UserProfile;
